/**
 * FilterChipsManager — admin CRUD for member-list filter chips.
 *
 * Org admins define the Quick Filter pills that appear inside the
 * Advanced Filters panel on the member list page. Each chip has:
 *   - key (URL-safe, unique)
 *   - label (display text)
 *   - description, sort_order, default_on
 *   - predicate (the server-side filter expression)
 *
 * Predicate forms (chosen via the dropdown):
 *   mo_status     — match member_organizations.status (active/inactive)
 *   custom_field  — match a com_fields value (field name + value)
 *   column        — match a column on members table (field + value)
 *   consent_status— match consent rollup
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, GripVertical, Save } from 'lucide-react';
import { apiClient } from '@/services/apiClient';

interface Chip {
  key: string;
  label: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order: number;
  default_on: boolean;
  predicate?: any;
}

const PREDICATE_TYPES = [
  { value: 'mo_status', label: 'Membership status (active / inactive)' },
  { value: 'custom_field', label: 'Custom field value' },
  { value: 'column', label: 'Member table column' },
  { value: 'consent_status', label: 'Consent status' },
];

export default function FilterChipsManager() {
  const [chips, setChips] = useState<Chip[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get('/api/members/filter-chips')
      .then(r => setChips(r.data?.chips || []))
      .catch(e => setError(e?.response?.data?.detail || 'Failed to load chips'))
      .finally(() => setLoading(false));
  }, []);

  const addChip = () => {
    setChips([
      ...chips,
      {
        key: `chip_${Date.now()}`,
        label: 'New chip',
        sort_order: chips.length,
        default_on: false,
        predicate: { type: 'mo_status', value: 'active' },
      },
    ]);
  };

  const updateChip = (i: number, patch: Partial<Chip>) => {
    setChips(chips.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };

  const removeChip = (i: number) => {
    setChips(chips.filter((_, idx) => idx !== i));
  };

  const moveChip = (i: number, dir: -1 | 1) => {
    const target = i + dir;
    if (target < 0 || target >= chips.length) return;
    const next = [...chips];
    [next[i], next[target]] = [next[target], next[i]];
    setChips(next.map((c, idx) => ({ ...c, sort_order: idx })));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.put('/api/members/filter-chips', { chips });
      alert('Filter chips saved. They will appear on the member list immediately.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save chips');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 text-sm text-gray-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-ink-500">
          Quick filters that appear on the Member List page inside Advanced Filters.
          Each chip applies a server-side predicate.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addChip} icon={<Plus className="w-4 h-4" />}>Add chip</Button>
          <Button onClick={save} disabled={saving} icon={<Save className="w-4 h-4" />}>
            {saving ? 'Saving…' : 'Save all'}
          </Button>
        </div>
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded p-2">{error}</div>}
      {chips.length === 0 && (
        <Card><CardContent className="p-6 text-center text-gray-500">
          No filter chips configured. Click "Add chip" to create one.
        </CardContent></Card>
      )}
      <div className="space-y-3">
        {chips.map((c, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 mt-1">
                  <button onClick={() => moveChip(i, -1)} className="text-gray-400 hover:text-gray-700" title="Move up">↑</button>
                  <button onClick={() => moveChip(i, 1)} className="text-gray-400 hover:text-gray-700" title="Move down">↓</button>
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs uppercase text-gray-500">Key</label>
                    <Input value={c.key} onChange={e => updateChip(i, { key: e.target.value })} placeholder="e.g. active_only" />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-gray-500">Label</label>
                    <Input value={c.label} onChange={e => updateChip(i, { label: e.target.value })} placeholder="e.g. Active" />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-gray-500">Description</label>
                    <Input value={c.description || ''} onChange={e => updateChip(i, { description: e.target.value })} placeholder="Tooltip text" />
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs uppercase text-gray-500">Predicate type</label>
                    <select
                      value={c.predicate?.type || 'mo_status'}
                      onChange={e => updateChip(i, { predicate: { ...(c.predicate || {}), type: e.target.value } })}
                      className="w-full h-9 text-sm border border-gray-300 rounded px-2"
                    >
                      {PREDICATE_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  {c.predicate?.type === 'custom_field' || c.predicate?.type === 'column' ? (
                    <div>
                      <label className="text-xs uppercase text-gray-500">Field name</label>
                      <Input value={c.predicate?.field || ''} onChange={e => updateChip(i, { predicate: { ...(c.predicate || {}), field: e.target.value } })} placeholder="e.g. enhanced_care_management_status" />
                    </div>
                  ) : null}
                  <div>
                    <label className="text-xs uppercase text-gray-500">Value</label>
                    <Input value={c.predicate?.value || ''} onChange={e => updateChip(i, { predicate: { ...(c.predicate || {}), value: e.target.value } })} placeholder="e.g. active / EN" />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input type="checkbox" checked={c.default_on} onChange={e => updateChip(i, { default_on: e.target.checked })} />
                    <span className="text-sm">Default on</span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeChip(i)} className="text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
